/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react'
import ReactDOM from 'react-dom'

// eslint-disable-next-line import/no-extraneous-dependencies
import { createUseStyles } from 'react-jss'
import breakpoints from '../../../theme/breakpoints'
import { colors } from '../../../theme/palette'
import { zIndexes } from '../../../theme/zIndex'
import { Close as CloseIcon } from '../../../assets/icons'

interface IProps {
    children?: React.ReactNode
    isShowing: boolean
    hide: () => void
    hideHeader?: boolean
    level?: number
    useBackdropColor?: boolean
}

interface StyleProps {
    useBackdropColor?: boolean
}

const useStyles = createUseStyles<string, StyleProps>((theme) => ({
    '@keyframes slideRight': {
        from: {
            transform: 'translateX(100%)',
        },
        to: {
            transform: 'translateX(0)',
        },
    },
    container: {
        color: theme.palette.secondary,
        height: '100dvh',
        width: '100%',
        maxWidth: '100%',
        position: 'fixed',
        zIndex: 4,
        top: 0,
        backgroundColor: theme.palette.navBackgroundColor,
        visibility: 'hidden',
        overflow: 'hidden',
        transform: 'translateX(100%)',
        transition: '.3s ease-in',
        right: 0,
        boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.25)',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                width: '500px',
            },
        },
        '&.active': {
            transform: 'translateX(0)',
            visibility: 'visible',
        },
    },
    button: {
        position: 'absolute',
        zIndex: zIndexes.drawer + 1,
        right: 0,
        top: 0,
        display: 'inline-block',
        margin: '20px 48px 0',
        borderRadius: '6px',
        boxSizing: 'border-box',
        textDecoration: 'none',
        fontWeight: 400,
        textAlign: 'center',
        transition: 'all 0.2s',
        background: 'transparent',
        boxShadow: 0,
        height: '10px',
        width: '10px',
        '&:hover': {
            cursor: 'pointer',
        },
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        border: '0',
        '&:hover svg': {
            fill: colors.gray500,
        },
    },
    medium: {
        fontSize: '.875em',
    },
    large: {
        fontSize: '1rem',
    },
    xlarge: {
        fontSize: '1.125rem',
    },
    overlay: {
        position: 'fixed',
        left: '0',
        top: '0',
        height: '100dvh',
        width: '100vw',
        zIndex: '2',
        backgroundColor: ({ useBackdropColor }) =>
            useBackdropColor ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
    },
}))

const Overlay: React.FC<IProps> = ({
    children,
    isShowing,
    hide,
    useBackdropColor = false,
}) => {
    const node = React.useRef<HTMLDivElement>(null)
    const classes = useStyles({ useBackdropColor })
    const handleClickOutside = (e: any): void => {
        if (node && node.current && node.current.contains(e.target)) {
            return
        }

        if (
            node &&
            node.current &&
            e.target.classList.contains('ignore-clickoutside')
        ) {
            return
        }

        // outside click
        if (isShowing) {
            if (hide) hide()
        }
    }

    React.useEffect(() => {
        if (isShowing) {
            const documentWidth = document.documentElement.clientWidth
            const windowWidth = window.innerWidth
            const scrollBarWidth = windowWidth - documentWidth

            document.body.style.overflow = 'hidden'
            document.body.style.paddingRight = `${scrollBarWidth}px`
            document.body.classList.add('no-scroll')
        } else {
            document.body.style.overflow = 'unset'
            document.body.style.paddingRight = 'unset'
            document.body.classList.remove('no-scroll')
        }
    }, [isShowing])

    React.useEffect(() => {
        // add when mounted
        document.addEventListener('keydown', onKeyPressed)
        document.addEventListener('mousedown', handleClickOutside)

        // return function to be called when unmounted
        return () => {
            document.removeEventListener('keydown', onKeyPressed)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    })

    const onKeyPressed = (e: any): void => {
        if (e.key === 'Escape' && hide && isShowing) {
            e.preventDefault()
            hide()
        }
    }

    return ReactDOM.createPortal(
        <>
            {isShowing ? (
                <div
                    className={classes.overlay}
                    onClick={() => hide()}
                    tabIndex={-1}
                    role="button"
                    aria-label="overlay"
                    onKeyDown={() => console.log('key down')}
                />
            ) : null}
            <div
                className={`${classes.container} ${
                    isShowing ? 'active' : 'hidden'
                }`}
                ref={node}
            >
                <button onClick={hide} className={classes.button} type="button">
                    <CloseIcon width="38px" color={colors.green500} />
                </button>
                <>{children && children}</>
            </div>
        </>,
        document.getElementById('root') as HTMLElement
    )
}

export { Overlay }
